<template>
  <div class="cardnews" @contextmenu.prevent>
    <div v-if="loading" class="cardnews__state">불러오는 중입니다…</div>

    <div v-else-if="error" class="cardnews__state">
      <p>{{ error }}</p>
    </div>

    <div v-else id="wrap">
      <swiper ref="mySwiper" :options="swiperOptions" @slideChange="onSlideChange">
        <swiper-slide v-for="slide in slides" :key="slide.seq">
          <div class="cardnews__slide">
            <img :src="imageUrl(slide)" :alt="`${title} ${slide.seq}`" />
            <!--
              버튼 영역은 이미지 위에 얹는 투명 링크다.
              좌표는 어드민에서 원본 px 로 입력한 값을 서버가 % 로 환산해 둔 것이라
              화면 폭과 무관하게 항상 이미지 속 버튼 위에 정확히 얹힌다.
            -->
            <template v-for="link in slide.links">
              <!--
                동영상 영역은 버튼과 같은 좌표계를 쓰되 <a> 대신 iframe 을 얹는다.
                swiper-no-swiping 이 없으면 플레이어를 만지는 순간 스와이프로 먹혀
                재생바를 끌 수 없다.
              -->
              <div
                v-if="isVideo(link)"
                :key="link.id"
                class="cardnews__video swiper-no-swiping"
                :style="rectStyle(link.rect)"
              >
                <iframe
                  :src="link.url"
                  :title="link.label"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                  referrerpolicy="strict-origin-when-cross-origin"
                  allowfullscreen
                ></iframe>
              </div>
              <a
                v-else
                :key="link.id"
                class="cardnews__hotspot"
                :style="rectStyle(link.rect)"
                :aria-label="link.label"
                role="link"
                tabindex="0"
                @click="onLinkClick(link)"
                @keyup.enter="onLinkClick(link)"
              ></a>
            </template>
          </div>
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<script>
import "swiper/dist/css/swiper.css";
import { swiper, swiperSlide } from "vue-awesome-swiper";
import { openExternalBrowser } from "@/utils/openExternal.js";
import { requestAccess, loadManifest, sendEvent, sendBeaconEvent } from "@/utils/cardnews";

/** 스와이프 도중 발생한 탭은 클릭으로 치지 않는다(오클릭 · 집계 부풀림 방지) */
const SWIPE_TAP_GUARD_MS = 250;

export default {
  components: { swiper, swiperSlide },

  data() {
    return {
      loading: true,
      error: "",
      title: "",
      baseUrl: "",
      /** 카드 이미지 바깥 여백에 깔 색. manifest 에 없으면 기본 배경을 그대로 쓴다. */
      bgColor: "",
      /** 배경색을 되돌리기 위해 바꾸기 전 인라인 값을 보관한다 */
      bgRestore: [],
      slides: [],
      maxSlide: 1,
      startedAt: Date.now(),
      lastSlideChangeAt: 0,
      progressSent: false,
      swiperOptions: {
        slidesPerView: 1,
        spaceBetween: 0,
        observer: true,
        observeParents: true,
        // .swiper-no-swiping 이 붙은 요소(동영상 플레이어) 위에서는 스와이프하지 않는다
        noSwiping: true,
      },
    };
  },

  computed: {
    slug() {
      return this.$route.params.slug;
    },
  },

  async created() {
    await this.load();
  },

  mounted() {
    // 이탈 시점에 진행 상황을 1건만 보낸다.
    // 슬라이드를 넘길 때마다 보내면 발송 직후 요청이 슬라이드 수만큼 곱해진다.
    window.addEventListener("pagehide", this.flushProgress);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },

  beforeDestroy() {
    this.clearBgColor();
    this.flushProgress();
    window.removeEventListener("pagehide", this.flushProgress);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },

  methods: {
    async load() {
      try {
        // 인증 확인 + CloudFront 서명 쿠키 발급.
        // 이후 manifest 와 이미지는 CDN 에서 직접 읽으므로 WAS 를 거치지 않는다.
        const access = await requestAccess(this.slug);
        this.title = access.title;
        this.baseUrl = access.baseUrl;

        const manifest = await loadManifest(access.manifestUrl);
        // 배경색은 어드민에서 지정한 카드뉴스에만 들어 있다.
        // 예전에 발행한 manifest 에는 키가 없어 기본 배경이 그대로 유지된다.
        this.applyBgColor(manifest.bgColor);
        this.slides = (manifest.slides || []).map((s) => ({
          ...s,
          links: s.links || [],
        }));

        if (!this.slides.length) {
          this.error = "카드뉴스 내용을 찾을 수 없습니다.";
          return;
        }

        this.startedAt = Date.now();
        sendEvent({ slug: this.slug, eventType: "VIEW" });
      } catch (e) {
        const status = e && e.response && e.response.status;
        // 인증이 없어서 401 인 경우에만 로그인으로 보낸다.
        // 로그인 상태인데도 401/403 이면 로그인으로 돌려봐야 /login 가드가 다시 이리로
        // 되돌려 보내 무한 왕복이 된다. 그때는 화면에 원인을 남긴다.
        if ((status === 401 || status === 403) && !this.$store.getters.isAuthorized) {
          this.$router.replace({ path: "/login", query: { returnTo: this.$route.path } });
          return;
        }
        this.error =
          status === 401 || status === 403
            ? "카드뉴스를 볼 권한을 확인하지 못했습니다. 다시 로그인해 주세요."
            : "카드뉴스를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.";
        console.error("[cardnews] load 실패:", status || "", e);
      } finally {
        this.loading = false;
      }
    },

    /**
     * 카드 이미지 아래위에 남는 여백은 이 컴포넌트가 아니라 회원 웹 공통 레이아웃
     * (.v-application--wrap)이 칠한다. 컴포넌트 안쪽만 칠하면 이미지보다 화면이 길 때
     * 남는 영역에 공통 배경색이 그대로 보여 카드와 이어지지 않는 것처럼 보인다.
     * 그래서 공통 레이아웃과 문서 배경까지 함께 덮어쓰고, 화면을 떠날 때 되돌린다.
     */
    applyBgColor(color) {
      if (!color || !/^#[0-9A-Fa-f]{6}$/.test(color)) return;
      this.bgColor = color;
      const targets = [
        document.documentElement,
        document.body,
        document.querySelector(".v-application--wrap"),
      ].filter(Boolean);
      this.bgRestore = targets.map((el) => ({ el, prev: el.style.backgroundColor }));
      // 공통 레이아웃의 배경색은 선택자 두 개짜리 규칙이라 인라인 스타일로 덮어쓴다
      targets.forEach((el) => {
        el.style.backgroundColor = color;
      });
    },

    /** 다른 화면으로 이동하면 원래 배경으로 되돌린다(SPA 라 화면이 새로 뜨지 않는다) */
    clearBgColor() {
      this.bgRestore.forEach(({ el, prev }) => {
        el.style.backgroundColor = prev;
      });
      this.bgRestore = [];
    },

    imageUrl(slide) {
      return this.baseUrl + slide.image;
    },

    /** kind 가 없는 기존 manifest 는 전부 버튼이다 */
    isVideo(link) {
      return link.kind === "VIDEO";
    },

    rectStyle(rect) {
      return {
        left: rect.left + "%",
        top: rect.top + "%",
        width: rect.width + "%",
        height: rect.height + "%",
      };
    },

    onSlideChange() {
      const sw = this.$refs.mySwiper && this.$refs.mySwiper.swiper;
      if (!sw) return;
      this.lastSlideChangeAt = Date.now();
      this.maxSlide = Math.max(this.maxSlide, sw.activeIndex + 1);
    },

    onLinkClick(link) {
      // 슬라이드를 넘기려던 드래그가 탭으로 잡히는 것을 막는다
      if (Date.now() - this.lastSlideChangeAt < SWIPE_TAP_GUARD_MS) return;

      sendEvent({ slug: this.slug, eventType: "CLICK", linkId: link.id });
      // 카카오톡 인앱 브라우저에서는 외부 브라우저로 넘긴다
      openExternalBrowser(link.url);
    },

    onVisibilityChange() {
      if (document.visibilityState === "hidden") this.flushProgress();
    },

    flushProgress() {
      if (this.progressSent || this.loading || this.error) return;
      this.progressSent = true;
      sendBeaconEvent({
        slug: this.slug,
        eventType: "PROGRESS",
        maxSlide: this.maxSlide,
        dwellMs: Date.now() - this.startedAt,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cardnews {
  width: 100%;
}

.cardnews__state {
  padding: 3rem 1.5rem;
  text-align: center;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.6;
}

.cardnews__slide {
  position: relative;
  width: 100%;
  line-height: 0;

  img {
    display: block;
    width: 100%;
    height: auto;
  }
}

/* 이미지 위에 얹히는 동영상 플레이어 */
.cardnews__video {
  position: absolute;
  overflow: hidden;
  background: #000;

  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }
}

/* 이미지에 그려진 버튼 위에 얹히는 투명 영역 */
.cardnews__hotspot {
  position: absolute;
  display: block;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.08);

  &:focus-visible {
    outline: 3px solid #1a73e8;
    outline-offset: 2px;
  }
}
</style>
